<template lang="pug">
ds-panel.tg-chat-relink-modal(space="lg")
  ds-panel-element(offset="bottom")
    ds-caption(
      variant="strong"
      :size="['4']"
    ) {{ _('Добавить привязки к Telegram-чату') }}

  ds-panel-element(offset="bottom")
    ds-caption(size="sm") {{ chatInfo }}

  //- выбираем, кого добавляем — клиентов или лидов.
  ds-panel-element(offset="bottom")
    form-line
      form-cell(cols="12")
        f-select(
          name="ownerType"
          :data="form"
          :super-label="_('Тип привязки')"
          label="title"
          track-by="id"
          :allow-empty="false"
          :options="ownerTypeOptions"
          @select="handleOwnerTypeSelect"
        )

  //- можно выбрать сразу несколько клиентов/лидов.
  ds-panel-element(offset="bottom")
    form-line
      form-cell(cols="12")
        f-select(
          name="items"
          :data="form"
          :super-label="isLeadMode ? _('Лиды') : _('Клиенты')"
          :label="itemLabel"
          track-by="id"
          :allow-empty="true"
          :preselect-first="false"
          :multiple="true"
          rules="required"
          @search-change="$event => { itemsParams.filters.search = $event; receiveItems(itemsParams); }"
          :options="itemsList || []"
          :loading="itemsLoading"
        )
          template(#afterList="")
            li
              div(
                v-if="!itemsLoading && itemsParams.pagination.total > itemsParams.pagination.offset + itemsParams.pagination.limit"
                v-observe-visibility="visible => getMoreItems(visible)"
              )

  ds-panel-element(offset="top")
    form-line
      form-cell(cols="narrow")
        control-button(
          styling="primary"
          :disabled="isSubmitting || !form.items.length"
          @click.prevent="submit"
        ) {{ _('Добавить') }}

      form-cell(cols="narrow")
        control-button(
          styling="default"
          :disabled="isSubmitting"
          @click.prevent="close"
        ) {{ _('Отмена') }}
</template>

<script>
import { clone } from 'ramda'

import { clients } from '@md/clients/api'
import { leads } from '@md/leads/api'
import { tgChatApi } from '@md/tg-chat/api'

export default {
  name: 'TelegramChatAddOwnersModal',

  props: {
    chat: {
      type: Object,
      required: true,
    },
    promise: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      form: {
        ownerType: {
          id: 'client',
          title: this._('Клиент'),
        },
        items: [],
      },

      itemsList: [],
      itemsLoading: false,
      itemsParams: {
        pagination: {},
        filters: {},
      },

      isSubmitting: false,
    }
  },

  computed: {
    ownerTypeOptions() {
      return [
        {
          id: 'client',
          title: this._('Клиент'),
        },
        {
          id: 'lead',
          title: this._('Лид'),
        },
      ]
    },

    isLeadMode() {
      return this.form.ownerType?.id === 'lead'
    },

    searchResource() {
      return this.isLeadMode
        ? leads.list
        : clients.list
    },

    itemLabel() {
      return this.isLeadMode
        ? '_telegramSelectTitle'
        : 'objId'
    },

    // уже привязанных владельцев повторно в select не показываем.
    linkedOwnerIds() {
      const owners = this.isLeadMode
        ? (this.chat?.leads || [])
        : (this.chat?.clients || [])

      return owners.map(owner => String(owner.id))
    },

    chatInfo() {
      return [
        this.chat?.tgUserId
          ? `${this._('Telegram ID')}: ${this.chat.tgUserId}`
          : '',
        this.chat?.username
          ? `@${this.chat.username}`
          : '',
        this.chat?.phone || '',
      ].filter(Boolean).join(' • ')
    },
  },

  mounted() {
    this.receiveItems({
      limit: 20,
    })
  },

  methods: {
    handleOwnerTypeSelect(ownerType) {
      this.form.ownerType = ownerType
      this.form.items = []

      this.itemsList = []
      this.itemsParams = {
        pagination: {},
        filters: {},
      }

      this.receiveItems({
        limit: 20,
      })
    },

    extractListItems(response) {
      if (Array.isArray(response?.items)) {
        return response.items
      }

      if (Array.isArray(response?.data?.items)) {
        return response.data.items
      }

      if (Array.isArray(response)) {
        return response
      }

      return []
    },

    receiveItems(parameters) {
      const param = clone(parameters)

      if (param.filters) {
        param.filters = JSON.stringify(parameters.filters)
      }

      this.itemsLoading = true

      return this.searchResource.execute(param)
        .then(res => {
          const items = this.extractListItems(res)
            // не предлагаем уже существующие связи.
            .filter(item => (
              !this.linkedOwnerIds.includes(String(item.id))
            ))
            .map(item => this.normalizeSelectItem(item))

          if (parameters.offset) {
            this.itemsList.push(...items)
          } else {
            this.itemsList = items
          }

          this.itemsParams.pagination = res.pagination || {}
        })
        .catch(() => {
          this.itemsList = []

          this.$toast.error(
            this._('Не удалось загрузить список.')
          )
        })
        .finally(() => {
          this.itemsLoading = false
        })
    },

    getMoreItems(isVisible) {
      if (this.itemsLoading || !isVisible) {
        return
      }

      const parameters = {
        ...this.itemsParams,
        limit: this.itemsParams.pagination.limit,
        offset:
          this.itemsParams.pagination.offset
          + this.itemsParams.pagination.limit,
      }

      this.receiveItems(parameters)
    },

    normalizeSelectItem(item) {
      return {
        ...item,
        _telegramSelectTitle: this.getItemTitle(item),
      }
    },

    getItemTitle(item) {
      if (!item) {
        return ''
      }

      const fullName = [
        item.name,
        item.middleName || item.middle_name,
        item.surname,
      ].filter(Boolean).join(' ')

      return (
        item.objId
        || item.obj_id
        || item.displayName
        || item.display_name
        || item.title
        || fullName
        || item.mainPhone
        || item.main_phone
        || item.email
        || `#${item.id}`
      )
    },

    // /actions/link/ теперь добавляет связи,
    // не заменяя уже существующих владельцев.
    getPayload() {
      const ids = this.form.items.map(item => item.id)

      return this.isLeadMode
        ? {
          leads: ids,
        }
        : {
          users: ids,
        }
    },

    async submit() {
      if (
        this.isSubmitting
        || !this.form.items.length
        || !(this.chat && this.chat.id)
      ) {
        return
      }

      this.isSubmitting = true

      try {
        const response = await tgChatApi.chat.link.execute(
          {
            chatId: this.chat.id,
          },
          this.getPayload()
        )

        this.$toast.success(
          this._('Привязки успешно добавлены.')
        )

        if (this.promise) {
          await this.promise(response)
        }

        this.$emit('close')
      } catch (error) {
        this.$toast.error(
          this._('Не удалось добавить привязки к Telegram-чату.')
        )
      } finally {
        this.isSubmitting = false
      }
    },

    close() {
      if (this.isSubmitting) {
        return
      }

      this.$emit('close')
    },
  },
}
</script>